import { Box, List, ListItem, ListItemText, makeStyles, Typography } from "@material-ui/core";
import { ArrowForwardIos } from "@material-ui/icons";
import EmojiObjectsIcon from "@material-ui/icons/EmojiObjects";
import * as React from "react";
import { useHistory } from "react-router";
import { useRecoilCallback, useRecoilValue } from "recoil";

import { CustomButton } from "@/components/Button";
import { Loading } from "@/components/Loading";
import { layoutTokens as layout, semanticColors as colors, textStyles } from "@/designTokens";
import { useDevice } from "@/hooks/useDevice";
import { BaseTemplate } from "@/layouts/BaseTemplate";
import { PropertyEditPageState } from "@/view_models/atoms";
import { SelectedProperty } from "@/view_models/propertySelectors";

import { ChangePropertyImageModal } from "./components/ChangePropertyImageModal";
import { DeletePropertyModal } from "./components/DeletePropertyModal";
import { useManagementDetailActions } from "./hooks/useManagementDetailActions";

const useStyles = makeStyles({
  content: { width: "100%", maxWidth: 776, padding: layout.space[24] },
  title: { ...textStyles["PC/Heading/H2"], marginBottom: layout.space[24] },
  list: { padding: 0, background: colors["surface/card"], borderRadius: layout.radius.lg, overflow: "hidden" },
  item: {
    padding: layout.space[16],
    borderBottom: `1px solid ${colors["border/default"]}`,
    "&:last-child": { borderBottom: 0 },
  },
  itemText: { margin: 0 },
  itemArrow: { display: "flex", alignItems: "center", marginLeft: layout.space[8] },
  icon: { fontSize: 12, color: colors["icon/default"] },
  infoBanner: {
    padding: layout.space[16],
    background: colors["status/info-bg"],
    borderRadius: layout.radius.md,
    marginBottom: layout.space[24],
  },
  infoBannerContent: { display: "flex", alignItems: "center", gap: layout.space[8] },
  deleteSection: { paddingTop: layout.space[32] },
  deleteButton: {
    color: colors["text/error"],
    borderColor: colors["status/error"],
    background: colors["surface/card"],
  },
});

type SettingItem = {
  id: string;
  label: string;
  onClick: () => void;
};

export const ManagementDetailSettingsPage: React.FC = () => {
  const { isMobile } = useDevice();
  const classes = useStyles();
  const history = useHistory();
  const property = useRecoilValue(SelectedProperty);

  const {
    openDeletePropertyModal,
    openChangeImagePropertyModal,
    openDeleteModal,
    openImageModal,
    closeDeleteModal,
    closeImageModal,
    handleDeleteProperty,
    handleUpdateImage,
  } = useManagementDetailActions();

  const updatePropertyEditPageState = useRecoilCallback(({ snapshot, set }) => async (newState) => {
    set(PropertyEditPageState, newState);
    await snapshot.getPromise(PropertyEditPageState);
  });

  const goEdit = async (state: string) => {
    await updatePropertyEditPageState(state);
    history.push("/edit_property");
  };

  if (!property) return <Loading />;

  const managementCostState =
    property.property.property_category === "一棟物件" ? "management_building_cost" : "management_cost";

  const items: SettingItem[] = [
    { id: "property_info", label: "物件の基本情報", onClick: () => goEdit("property_info") },
    { id: "purchase_info", label: "物件購入時の情報", onClick: () => goEdit("purchase_info") },
    { id: "management_cost", label: "経費情報", onClick: () => goEdit(managementCostState) },
    { id: "loan_info", label: "借入情報", onClick: () => goEdit("loan_info") },
    { id: "insurance_info", label: "保険情報", onClick: () => goEdit("insurance_info") },
    { id: "property_image", label: "物件画像の変更", onClick: () => openImageModal() },
  ];

  if (property.property.property_category !== "一棟物件") {
    items.splice(3, 0, { id: "lease_contract", label: "賃貸契約情報", onClick: () => goEdit("lease_contract") });
  }

  const infoBannerText =
    property.property.property_category === "一棟物件"
      ? "家賃収入を変更したい場合は、物件詳細より部屋の追加・編集を選択してください"
      : "家賃収入を変更したい場合は賃貸借契約情報を変更してください";

  return (
    <BaseTemplate headerTitle={isMobile ? "物件情報編集" : undefined} onBack={() => history.goBack()} isDense isPcV2>
      <Box display="flex" justifyContent="center">
        <Box className={classes.content}>
          {!isMobile && (
            <Typography component="h1" className={classes.title}>
              物件情報編集
            </Typography>
          )}
          <Box className={classes.infoBanner}>
            <Box className={classes.infoBannerContent}>
              <EmojiObjectsIcon htmlColor={colors["icon/primary"]} />
              <Typography variant="body2">{infoBannerText}</Typography>
            </Box>
          </Box>
          <List className={classes.list}>
            {items.map((item) => (
              <ListItem key={item.id} button onClick={item.onClick} className={classes.item}>
                <ListItemText
                  classes={{ root: classes.itemText }}
                  primary={<Typography variant="body2">{item.label}</Typography>}
                />
                <Box className={classes.itemArrow}>
                  <ArrowForwardIos className={classes.icon} />
                </Box>
              </ListItem>
            ))}
          </List>
          <Box className={classes.deleteSection}>
            <CustomButton customVariant="outlinedPrimary" className={classes.deleteButton} onClick={openDeleteModal}>
              物件を削除する
            </CustomButton>
          </Box>
        </Box>
      </Box>

      <DeletePropertyModal
        open={openDeletePropertyModal}
        onClose={closeDeleteModal}
        onClickDelete={async () => handleDeleteProperty(property.property.id)}
      />
      <ChangePropertyImageModal
        open={openChangeImagePropertyModal}
        onClose={closeImageModal}
        onClickChangeImage={async (imageFile) => handleUpdateImage(property.property.id, imageFile)}
      />
    </BaseTemplate>
  );
};
